import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkSupabaseConnection } from '../supabaseClient';
import { Card } from './common';
import Header from './common/Header';
import { theme } from '../styles/theme';

const FeatureCard = ({ title, description, icon, onClick }) => {
  const cardStyle = {
    background: `linear-gradient(to bottom right, white, ${theme.colors.primary.light})`,
    borderColor: theme.colors.neutral.light
  };

  const iconStyle = {
    background: `linear-gradient(to bottom right, ${theme.colors.primary.main}, ${theme.colors.primary.dark})`
  };

  const hoverLineStyle = {
    background: `linear-gradient(to right, ${theme.colors.accent.main}, ${theme.colors.accent.hover})`
  };

  return (
    <div onClick={onClick} className="cursor-pointer group">
      <div style={cardStyle} className={`
        relative overflow-hidden rounded-2xl p-8
        transition-all duration-300
        hover:shadow-xl hover:-translate-y-1
        border border-neutral-200
      `}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ 
            backgroundImage: `radial-gradient(circle at 2px 2px, ${theme.colors.primary.main} 1px, transparent 0)`,
            backgroundSize: '24px 24px'
          }} />
        </div>

        {/* Content */}
        <div className="relative z-10">
          <div className="flex items-start space-x-6 mb-6">
            <div 
              style={iconStyle} 
              className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 text-white text-2xl"
            >
              {icon}
            </div>
            <div>
              <h3 
                style={{ color: theme.colors.text.primary }} 
                className="text-xl font-bold mb-2"
              >
                {title}
              </h3>
              <p 
                style={{ color: theme.colors.text.secondary }} 
                className="text-base leading-relaxed"
              >
                {description}
              </p>
            </div>
          </div>
        </div>

        {/* Hover Effect */}
        <div style={hoverLineStyle} className="absolute bottom-0 left-0 right-0 h-1 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
      </div>
    </div>
  );
};

const MaxComply = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [isSupabaseConnected, setIsSupabaseConnected] = useState(false);
  const [connectionError, setConnectionError] = useState('');

  useEffect(() => {
    const checkConnection = async () => {
      const isConnected = await checkSupabaseConnection();
      setIsSupabaseConnected(isConnected);
      if (!isConnected) {
        setConnectionError('Unable to connect to the database. Please check your internet connection and try again.');
      }
    };
    checkConnection();
  }, []);

  const features = [
    {
      title: 'Audit Program',
      description: 'Comprehensive audit planning and scheduling system for quality assurance.',
      icon: '📋',
      path: '/max-comply/audit-program'
    },
    {
      title: 'QA Certifications',
      description: 'Manage and track quality assurance certifications and qualifications.',
      icon: '🎓',
      path: '/max-comply/qa-certifications'
    },
    {
      title: 'QA Training',
      description: 'Quality assurance training management and tracking.',
      icon: '👥',
      path: '/max-comply/qa-training'
    }
  ];

  if (!isSupabaseConnected) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-center text-gray-500">
          Connecting to the database...
        </p>
      </div>
    );
  }

  if (connectionError) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{connectionError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header 
        user={user} 
        setUser={setUser}
        title="Max_Comply"
      />

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <h1 
            className="text-3xl font-bold mb-2" 
            style={{ color: theme.colors.text.primary }}
          >
            Quality Assurance Management
          </h1>
          <p 
            className="text-xl mb-8" 
            style={{ color: theme.colors.text.secondary }}
          >
            Comprehensive tools for managing aviation quality assurance programs
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <FeatureCard 
                key={index} 
                {...feature} 
                onClick={() => navigate(feature.path)}
              />
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default MaxComply;
